import {
  TSUYAKU_LANGUAGE_LABELS,
  type TsuyakuOutputLanguage,
} from "../../lib/api-types";

export interface TsuyakuTurn {
  id: string;
  /** Target language for this turn — the user's selected output language at
   *  the time the turn started. The source language is auto-detected by the
   *  model, so we display only an `auto → target` direction. */
  target: TsuyakuOutputLanguage;
  /** Source-language transcript (input). */
  input: string;
  /** Target-language transcript (translation). */
  output: string;
  /** Latency from PTT release → first output_audio_transcript.delta, seconds. */
  latencySeconds: number | null;
  /** True once the transcript is finalised. */
  done: boolean;
}

export function TurnHistory({ turns }: { turns: TsuyakuTurn[] }) {
  if (turns.length === 0) {
    return (
      <div className="flex min-h-[96px] items-center justify-center rounded-box border border-dashed border-rule bg-cream-soft px-4 py-6 font-mono text-[11px] tracking-widest text-subtext">
        AWAITING TX · PRESS PTT TO TRANSMIT
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {turns.map((turn) => (
        <TurnBubble key={turn.id} turn={turn} />
      ))}
    </div>
  );
}

function TurnBubble({ turn }: { turn: TsuyakuTurn }) {
  const targetCode = turn.target.toUpperCase();
  const targetLabel = TSUYAKU_LANGUAGE_LABELS[turn.target];
  const latencyLabel =
    turn.latencySeconds == null
      ? "—"
      : `${turn.latencySeconds.toFixed(1)}s`;

  return (
    <article className="rounded-box border border-ink/15 bg-cream-soft p-3">
      <header className="flex items-center justify-between pb-2 font-mono text-[10px] tracking-widest text-subtext">
        <span>auto → {targetCode}</span>
        <span>{turn.done ? `LAT ${latencyLabel}` : "LAT …"}</span>
      </header>

      <div className="space-y-2 text-[13px] leading-relaxed">
        <p className="text-subtext">
          <span className="font-mono text-[10px] tracking-wider text-ink/70">
            SRC ·{" "}
          </span>
          {turn.input || <span className="italic opacity-60">…</span>}
        </p>
        <p className="text-ink">
          <span className="font-mono text-[10px] tracking-wider text-rust">
            {targetCode} · {targetLabel} ·{" "}
          </span>
          {turn.output || <span className="italic opacity-60">…</span>}
        </p>
      </div>
    </article>
  );
}
